import { NavigationGroup, NAVIGATION_GROUP_LABELS, NAVIGATION_GROUP_ORDER } from '@/constants/residents/residentNavigation';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { View, Pressable, StyleSheet, ScrollView, Platform } from 'react-native';
import { SPACING, RADIUS } from '@/constants/shared/theme';
import { Text } from '@/components/common/text';
import { AnimatedAccordion } from '@/components/common/AnimatedAccordion';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import { WEB_STYLES } from '@/utils/webStyles';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

export type DesktopSidebarItem = {
  key: string;
  title: string;
  icon?: React.ReactNode;
  onPress: () => void;
  isActive?: boolean;
  group?: NavigationGroup;
};

type DesktopSidebarProps = {
  items: DesktopSidebarItem[];
  footer?: React.ReactNode;
};

const SidebarNavItem: React.FC<{ item: DesktopSidebarItem }> = React.memo(({ item }) => {
  const { colors } = useThemeColors();
  // `none`: the shared rest cursor/transition/no-select; the tint itself stays
  // the sidebar's own token, as GlassSidebarNavItem does.
  const { isHovered, hoverProps, viewStyle } = useHoverStyle('none');
  const isActive = item.isActive;

  return (
    <Pressable
      style={[
        styles.item,
        viewStyle,
        isHovered && !isActive && { backgroundColor: colors.sidebar.itemHoverBg },
        isActive && { backgroundColor: colors.sidebar.itemActiveBg },
      ]}
      onPress={item.onPress}
      {...hoverProps}
    >
      {isActive && (
        <View style={[styles.activeIndicator, { backgroundColor: colors.sidebar.indicator }]} />
      )}
      <View style={styles.itemIcon}>{item.icon}</View>
      <Text
        style={[
          styles.itemText,
          { color: colors.text.muted },
          isActive && { color: colors.text.secondary, fontWeight: '600' },
        ]}
        numberOfLines={1}
      >
        {item.title}
      </Text>
    </Pressable>
  );
}, (prev, next) => {
  return prev.item.key === next.item.key
    && prev.item.isActive === next.item.isActive
    && prev.item.title === next.item.title;
});

SidebarNavItem.displayName = 'SidebarNavItem';

const GroupSeparator = React.memo(() => {
  const { colors } = useThemeColors();
  return <View style={[styles.groupSeparator, { backgroundColor: colors.separator }]} />;
});
GroupSeparator.displayName = 'GroupSeparator';

export const DesktopSidebar: React.FC<DesktopSidebarProps> = ({ items, footer }) => {
  const { colors } = useThemeColors();
  // All groups start expanded on desktop — only 12 items across 3 groups,
  // so progressive disclosure adds clicks without benefit. User can still
  // manually collapse a group via the header toggle.
  const [expandedGroups, setExpandedGroups] = useState<Set<NavigationGroup>>(
    () => new Set(NAVIGATION_GROUP_ORDER),
  );
  // true only during the render triggered by a user click on a group header
  const isUserToggleRef = useRef(false);

  // Reset after each render so navigation-driven changes get animate=false
  useEffect(() => { isUserToggleRef.current = false; });

  const hasGroups = useMemo(() => items.some((item) => item.group), [items]);

  const groupedItems = useMemo(() => {
    if (!hasGroups) return null;
    return NAVIGATION_GROUP_ORDER.reduce((acc, group) => {
      acc[group] = items.filter((item) => item.group === group);
      return acc;
    }, {} as Record<NavigationGroup, DesktopSidebarItem[]>);
  }, [items, hasGroups]);

  const toggleGroup = useCallback((group: NavigationGroup) => {
    isUserToggleRef.current = true;
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  return (
    <View style={[styles.container, { backgroundColor: colors.bg.modal }]}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.list}>
          {hasGroups && groupedItems
            ? NAVIGATION_GROUP_ORDER.map((group, groupIndex) => {
                const sectionItems = groupedItems[group];
                if (!sectionItems || sectionItems.length === 0) return null;
                // Group is expanded if user toggled it open OR it contains the active item
                const groupHasActiveItem = sectionItems.some(item => item.isActive);
                const isExpanded = expandedGroups.has(group) || groupHasActiveItem;

                return (
                  <React.Fragment key={group}>
                    {groupIndex > 0 && <GroupSeparator />}
                    <AnimatedAccordion
                      isExpanded={isExpanded}
                      animate={isUserToggleRef.current}
                      onToggle={() => toggleGroup(group)}
                      containerStyle={styles.section}
                      headerStyle={styles.sectionHeaderButton}
                      contentStyle={styles.sectionItems}
                      header={
                        <Text style={[styles.sectionHeader, { color: colors.text.subtle }, isExpanded && { color: colors.text.secondary }]}>
                          {NAVIGATION_GROUP_LABELS[group]}
                        </Text>
                      }
                      chevron={
                        <RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN
                          width={16}
                          height={16}
                          color={isExpanded ? colors.text.secondary : colors.text.subtle}
                        />
                      }
                    >
                      {sectionItems.map((item) => (
                        <SidebarNavItem key={item.key} item={item} />
                      ))}
                    </AnimatedAccordion>
                  </React.Fragment>
                );
              })
            : items.map((item) => <SidebarNavItem key={item.key} item={item} />)}
        </View>
      </ScrollView>
      {footer ? <View style={styles.footer}>{footer}</View> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 264,
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  scrollView: {
    flex: 1,
    ...Platform.select({
      web: { scrollbarGutter: 'stable' } as any,
      default: {},
    }),
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
  },
  list: {
    flexGrow: 1,
    gap: 4,
  },
  section: {
    width: '100%',
  },
  // The nav items' shape (radius 12, 12pt inset), so the hover tint on a
  // group label is a rounded plate with air like theirs, and the label lines
  // up with the items under it.
  sectionHeaderButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: SPACING.md,
    borderRadius: RADIUS.md,
  },
  sectionHeader: {
    fontWeight: '600',
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  sectionItems: {
    width: '100%',
  },
  groupSeparator: {
    height: 1,
    marginVertical: 4,
    marginHorizontal: 4,
  },
  item: {
    minHeight: 44,
    borderRadius: 12,
    marginVertical: 1,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    ...WEB_STYLES.cursorPointer,
    ...WEB_STYLES.transition('background-color', '0.15s', 'ease'),
  } as any,
  activeIndicator: {
    position: 'absolute',
    left: 0,
    top: 8,
    bottom: 8,
    width: 3,
    borderTopRightRadius: 3,
    borderBottomRightRadius: 3,
  },
  itemIcon: {
    width: 32,
    alignItems: 'center',
    marginRight: 12,
  },
  itemText: {
    fontSize: 14,
    fontWeight: '500',
    flexShrink: 1,
  },
  footer: {
    marginTop: 12,
  },
});
